import React, { useEffect, useRef, useState, useCallback } from 'react'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { PanoramaHotspot } from '../types'

export interface PanoramaViewProps {
  imageUrl: string;
  className?: string;
  initialRotation?: {
    longitude: number; // in degrees (0 = forward, 90 = right, 180 = back, 270 = left)
    latitude: number; // in degrees (0 = horizon, 90 = up, -90 = down)
  };
  setNodeAngleFov?: React.Dispatch<React.SetStateAction<number | null>>;
  hotspots?: PanoramaHotspot[];
  onHotspotClick?: (hotspot: PanoramaHotspot) => void;
  onPanoramaClick?: (sphericalCoords: { theta: number; phi: number }) => void; // returns degrees
  isEditMode?: boolean;
}

export const PanoramaView: React.FC<PanoramaViewProps> = ({
  imageUrl,
  className = 'w-full h-full relative',
  initialRotation = { longitude: 0, latitude: 0 },
  setNodeAngleFov,
  hotspots = [],
  onHotspotClick,
  onPanoramaClick,
  isEditMode = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<number>()
  const controlsRef = useRef<OrbitControls | null>(null)
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null)
  const sceneRef = useRef<THREE.Scene | null>(null)
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
  const [hotspotScreenPositions, setHotspotScreenPositions] = useState<Array<{
    id: string;
    x: number;
    y: number;
    visible: boolean
  }>>([])

  // Utility functions for degree/radian conversion
  const degreesToRadians = (degrees: number) => degrees * (Math.PI / 180)
  const radiansToDegrees = (radians: number) => radians * (180 / Math.PI)

  const sphericalToScreenPosition = useCallback((thetaDegrees: number, phiDegrees: number) => {
    if (!cameraRef.current || !containerRef.current) return null

    // Convert degrees to radians for Three.js
    const thetaRad = degreesToRadians(thetaDegrees)
    const phiRad = degreesToRadians(phiDegrees)

    const vector = new THREE.Vector3()
    vector.setFromSphericalCoords(1, Math.PI / 2 - phiRad, thetaRad)
    vector.project(cameraRef.current)

    const container = containerRef.current
    const x = (vector.x + 1) * container.clientWidth / 2
    const y = (-vector.y + 1) * container.clientHeight / 2
    const visible = vector.z < 1

    return { x, y, visible }
  }, [])

  const screenToSphericalCoords = useCallback((screenX: number, screenY: number) => {
    if (!cameraRef.current || !containerRef.current) return null

    const container = containerRef.current
    const x = (screenX / container.clientWidth) * 2 - 1
    const y = -(screenY / container.clientHeight) * 2 + 1

    const vector = new THREE.Vector3(x, y, 0.5)
    vector.unproject(cameraRef.current)
    vector.sub(cameraRef.current.position).normalize()

    const spherical = new THREE.Spherical().setFromVector3(vector)
    return {
      theta: radiansToDegrees(spherical.theta), // Convert to degrees
      phi: radiansToDegrees(Math.PI / 2 - spherical.phi) // Convert to degrees
    }
  }, [])

  const updateHotspotPositions = useCallback(() => {
    if (!hotspots.length) return

    const positions = hotspots.map(hotspot => {
      const pos = sphericalToScreenPosition(
        hotspot.sphericalCoords.theta,
        hotspot.sphericalCoords.phi
      )
      return {
        id: hotspot.id,
        x: pos?.x || 0,
        y: pos?.y || 0,
        visible: pos?.visible || false
      }
    })

    setHotspotScreenPositions(positions)
  }, [hotspots, sphericalToScreenPosition])

  const handleCanvasClick = useCallback((event: MouseEvent) => {
    if (!isEditMode || !onPanoramaClick) return

    const container = containerRef.current
    if (!container) return

    const rect = container.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    const sphericalCoords = screenToSphericalCoords(x, y)
    if (sphericalCoords) {
      onPanoramaClick(sphericalCoords)
    }
  }, [isEditMode, onPanoramaClick, screenToSphericalCoords])

  // Initialize renderer only once
  useEffect(() => {
    const container = containerRef.current
    if (!container || rendererRef.current) return

    // Renderer configuration
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
    })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setSize(container.clientWidth, container.clientHeight)
    renderer.outputColorSpace = THREE.SRGBColorSpace
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    renderer.toneMappingExposure = 0.8

    const canvas = renderer.domElement
    canvas.style.position = 'absolute'
    canvas.style.top = '0'
    canvas.style.left = '0'
    canvas.style.width = '100%'
    canvas.style.height = '100%'
    canvas.style.cursor = 'grab'

    canvas.addEventListener('click', handleCanvasClick)

    rendererRef.current = renderer
    container.appendChild(canvas)

    return () => {
      if (rendererRef.current) {
        const canvas = rendererRef.current.domElement
        canvas.removeEventListener('click', handleCanvasClick)
        rendererRef.current.dispose()
        if (container.contains(canvas)) {
          container.removeChild(canvas)
        }
        rendererRef.current = null
      }
    }
  }, [handleCanvasClick])

  useEffect(() => {
    const container = containerRef.current
    const renderer = rendererRef.current
    if (!container || !renderer) return

    container.style.position = 'relative'
    container.style.overflow = 'hidden'

    // Scene setup
    const scene = new THREE.Scene()
    sceneRef.current = scene

    const camera = new THREE.PerspectiveCamera(
      75,
      container.clientWidth / container.clientHeight,
      0.1,
      1000
    )
    camera.position.set(0, 0, 0.1)
    cameraRef.current = camera

    // Panorama sphere
    const geometry = new THREE.SphereGeometry(500, 64, 64)
    geometry.scale(-1, 1, 1) // Invert the sphere so textures render inside

    const texture = new THREE.TextureLoader().load(imageUrl, () => {
      if (rendererRef.current && cameraRef.current && sceneRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current)
      }
    })
    texture.colorSpace = THREE.SRGBColorSpace
    texture.anisotropy = rendererRef.current?.capabilities.getMaxAnisotropy() || 1

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      toneMapped: true,
    })

    const sphere = new THREE.Mesh(geometry, material)
    scene.add(sphere)

    // Controls setup
    const controls = new OrbitControls(camera, rendererRef.current!.domElement)
    controlsRef.current = controls
    controls.enableZoom = false
    controls.enablePan = false
    controls.enableDamping = true
    controls.dampingFactor = 0.1
    controls.rotateSpeed = -0.7
    controls.screenSpacePanning = false
    controls.maxPolarAngle = Math.PI
    controls.minPolarAngle = 0

    // Set initial rotation
    const updateCameraRotation = () => {
      if (!cameraRef.current || !controlsRef.current) return

      const { longitude, latitude } = initialRotation
      // Convert degrees to radians for Three.js
      const longitudeRad = degreesToRadians(longitude)
      const latitudeRad = degreesToRadians(latitude)

      const spherical = new THREE.Spherical(
        1,
        Math.PI / 2 - latitudeRad,
        longitudeRad
      )
      const target = new THREE.Vector3().setFromSpherical(spherical)

      cameraRef.current.position.copy(target.multiplyScalar(0.1))
      controlsRef.current.target.set(0, 0, 0)
      controlsRef.current.update()
    }

    updateCameraRotation()

    // Animation loop
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate)
      controls.update()

      // Compute current view direction spherical angles
      if (cameraRef.current && setNodeAngleFov) {
        const dir = new THREE.Vector3()
        cameraRef.current.getWorldDirection(dir)
        const sphericalDir = new THREE.Spherical().setFromVector3(dir)
        // Pass horizontal angle (theta) as FOV for node highlighting - keep in radians for compatibility
        setNodeAngleFov(sphericalDir.theta)
      }

      // Update hotspot screen positions
      updateHotspotPositions()

      if (rendererRef.current) {
        rendererRef.current.render(scene, camera)
      }
    }
    animate()

    // Handle resize
    const onResize = () => {
      if (!cameraRef.current || !rendererRef.current) return
      cameraRef.current.aspect = container.clientWidth / container.clientHeight
      cameraRef.current.updateProjectionMatrix()
      rendererRef.current.setSize(container.clientWidth, container.clientHeight)
    }
    window.addEventListener('resize', onResize)

    // Cleanup
    return () => {
      window.removeEventListener('resize', onResize)
      cancelAnimationFrame(animationRef.current!)
      if (controlsRef.current) {
        controlsRef.current.dispose()
        controlsRef.current = null
      }
      geometry.dispose()
      material.dispose()
      texture.dispose()
      cameraRef.current = null
      sceneRef.current = null
    }
  }, [imageUrl, initialRotation, setNodeAngleFov, updateHotspotPositions])

  // Update rotation when prop changes
  useEffect(() => {
    if (!controlsRef.current || !cameraRef.current) return
    const { longitude, latitude } = initialRotation
    // Convert degrees to radians for Three.js
    const longitudeRad = degreesToRadians(longitude)
    const latitudeRad = degreesToRadians(latitude)

    const spherical = new THREE.Spherical(1, Math.PI / 2 - latitudeRad, longitudeRad)
    const target = new THREE.Vector3().setFromSpherical(spherical)

    controlsRef.current.target.set(0, 0, 0)
    cameraRef.current.position.copy(target.multiplyScalar(0.1))
    controlsRef.current.update()
  }, [initialRotation])

  return (
    <div ref={containerRef} className={className}>
      {hotspotScreenPositions.map((position) => {
        const hotspot = hotspots.find(h => h.id === position.id)
        if (!hotspot || !position.visible) return null

        return (
          <div
            key={hotspot.id}
            className="absolute cursor-pointer transform -translate-x-1/2 -translate-y-1/2 z-10"
            style={{
              left: `${position.x}px`,
              top: `${position.y}px`,
            }}
            onClick={(e) => {
              e.stopPropagation()
              onHotspotClick?.(hotspot)
            }}
            title={hotspot.targetRoomName}
          >
            <div className="relative">
              <div
                className="w-6 h-6 bg-blue-500 rounded-full border-2 border-white shadow-lg hover:bg-blue-600 transition-colors animate-pulse">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
              </div>
              <div
                className="absolute top-8 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs whitespace-nowrap">
                {hotspot.targetRoomName}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}